import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import DeviceThermostatOutlinedIcon from '@mui/icons-material/DeviceThermostatOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import MemoryIcon from '@mui/icons-material/Memory';
import { Box, Grid2, Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { StaticData } from '../../config/enum/staticData';
import { fetchDashboardEarnings, fetchDashboardMetrics, fetchStaticData } from '../../services/dashboard-service';
import {
    setCachedMetricsData,
    setEarningsData,
    setIsMetricsAPICalled, // saveHeaterDetails,

    // setIsMetricsAPICalled,
    setIsRefresh,
    setMetricsData, // saveHeaterDetails,
} from '../../store/reducers/heaterSlice';
import { updateStaticStateData } from '../../store/reducers/staticDataSlice';
import { generateInterpolatedColors } from '../../utils/generateColorArraya';
import { getDayDiff, getTimeAgo } from '../../utils/helper';
import { DashboardMetricsData } from '../../web-socket/socket.types';
import { useSocket } from '../../web-socket/useSocket';
import { WSInstance } from '../../web-socket/web-socket-service';
import { ListItemWrapper, StyleWrapper } from './home-styles';
import { ReactChart } from './react-chart';
import { NavigationConfig } from '../../utils/navigation-constants';

interface HomeProps {}
export const Home: React.FC<HomeProps> = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const isFocused = location.pathname === NavigationConfig.Home;

    const isRefresh = useSelector((state: any) => state.heater.is_refresh);
    const isMetricsAPICalled = useSelector((state: any) => state.heater.is_metrics_api_called);
    const dispatch = useDispatch();
    const token = useSelector((state: any) => state.app.session?.token);
    /* eslint-disable no-undef */
    const [chartData, setChartTemperatureData] = React.useState<{ labels: string[]; values: number[] }>({
        labels: [],
        values: [],
    });
    const [powerUpdatedAt, setPowerUpdatedAt] = React.useState<string>('--');
    const [waterUpdatedAt, setWaterUpdatedAt] = React.useState<string>('--');
    const machineId = useSelector((state: any) => state.heater.metricsData?.machine_id);
    const userId = useSelector((state: any) => state.user.userId);
    const metricsData = useSelector((state: any) => state.heater.metricsData);
    const showHeater = !metricsData?.machine_id;
    const earningsData = useSelector((state: any) => state.heater.earningsData);
    const temperatueHistory = useSelector((state: any) => state.heater.metricsData.temperature_history);
    const cachedData = useSelector((state: any) => state.heater.cachedData);
    const [cachedMetrics, setCachedMetrics] = React.useState<any>(null);
    const staticDataPayload = useSelector((state: any) => state?.staticDataState);
    const gradientColorArray = ['#2FABE1', '#59BDE7', '#B3E3F3', '#FDCC3F', '#FF8524', '#FF8524', '#E03C23', '#441212'];
    const tempStaticData = staticDataPayload?.[StaticData.TEMPERATURE];
    /* eslint-disable */
    const rangeArray = React.useMemo(() => {
        const result = Array.from(
            { length: tempStaticData?.maxValue - tempStaticData?.minValue + 1 },
            (_, i) => tempStaticData?.minValue + i,
        );
        return result;
    }, [tempStaticData?.minValue, tempStaticData?.maxValue]);

    const rangeColorArray = React.useMemo(() => {
        const diff = tempStaticData?.maxValue - tempStaticData?.minValue + 6;
        const stepsLength = generateInterpolatedColors(gradientColorArray, diff);
        return stepsLength;
    }, [tempStaticData?.minValue, tempStaticData?.maxValue]);

    const getStaticData = async () => {
        if (!token) return;
        const staticData = await fetchStaticData();
        if (staticData) {
            const tempStaticData = staticData?.[StaticData.TEMPERATURE];
            const privacyData = staticData?.[StaticData.PRIVACY_POLICY];
            if (tempStaticData) {
                const payload = {
                    minValue:
                        tempStaticData?.[0] && tempStaticData?.[0]?.key ? parseInt(tempStaticData?.[0]?.key, 10) : 105,
                    maxValue:
                        tempStaticData?.[1] && tempStaticData?.[1]?.key ? parseInt(tempStaticData?.[1]?.key, 10) : 135,
                };
                dispatch(
                    updateStaticStateData({
                        key: StaticData.TEMPERATURE,
                        value: payload,
                    }),
                );
            }
            if (privacyData) {
                const emailData = privacyData?.find((item: any) => item?.key == 'email')?.value;
                const privacyPolicyLinkData = privacyData?.find((item: any) => item?.key == 'link')?.value;
                const payload = {
                    emailId: emailData,
                    privacyPolicyLink: privacyPolicyLinkData,
                };
                dispatch(
                    updateStaticStateData({
                        key: StaticData.PRIVACY_POLICY,
                        value: payload,
                    }),
                );
            }
        }
    };

    React.useEffect(() => {
        getStaticData();
    }, [token]);
    React.useEffect(() => {
        if (isMetricsAPICalled && cachedMetrics === null) {
            setCachedMetrics({
                power_sensor_timestamp: metricsData?.power_sensor_timestamp,
                water_sensor_timestamp: metricsData?.water_sensor_timestamp,
                power_usage: metricsData?.power_usage,
                water_temperature: metricsData?.water_temperature,
            });
        }
    }, [metricsData, cachedMetrics, isMetricsAPICalled]);

    React.useEffect(() => {
        const newChartData = { labels: [], values: [] as number[] };
        if (temperatueHistory?.length > 1) {
            newChartData.labels = temperatueHistory.map((temperature: any) => temperature.date?.slice(0, 5));
            newChartData.values = temperatueHistory.map((temperature: any) => temperature.temp_avg);
        }
        setChartTemperatureData(prev => {
            // Only update if data actually changed
            if (
                JSON.stringify(prev.labels) !== JSON.stringify(newChartData.labels) ||
                JSON.stringify(prev.values) !== JSON.stringify(newChartData.values)
            ) {
                return newChartData;
            }
            return prev;
        });
    }, [temperatueHistory]);
    const fetchData = React.useCallback(async () => {
        const earnings = await fetchDashboardEarnings();
        const metrics = await fetchDashboardMetrics(true);

        if (!metrics) {
            dispatch(setIsMetricsAPICalled(false));
        }
        if (earnings) {
            dispatch(
                setEarningsData({
                    gpu_earning_month: earnings?.gpu_earning_month,
                    storage_earning_month: earnings?.storage_earning_month,
                    gpu_earning_total: earnings?.gpu_earning_total,
                    storage_earning_total: earnings?.storage_earning_total,
                    machine_name: earnings?.machine_name,
                    gpu_earnings_updated_at: earnings?.gpu_earnings_updated_at,
                }),
            );
            console.warn(earnings?.device_id, 'earnings device_id from api');
            console.warn(machineId, 'currentmachineId');
            if (machineId && earnings?.device_id !== machineId) {
                // clear the cached data
                dispatch(setCachedMetricsData({ [`${userId}_${machineId}`]: null }));
                console.warn(metrics, 'metrics in fetchData()');
                setCachedMetrics({
                    power_sensor_timestamp: metrics?.power_timestamp,
                    water_sensor_timestamp: metrics?.sensor_timestamp,
                    power_usage: metrics?.power_usage,
                    water_temperature: metrics?.water_temperature,
                    api_fetch_timestamp: new Date(),
                });
                WSInstance.disconnect();
                WSInstance.connect(token);
            }
        }
        if (metrics) {
            dispatch(setIsMetricsAPICalled(true));
            dispatch(
                setMetricsData({
                    power_sensor_timestamp: metrics?.power_timestamp,
                    water_sensor_timestamp: metrics?.sensor_timestamp,
                    avg_gpu_util_7d: metrics?.avg_gpu_util_7d,
                    avg_gpu_util_30d: metrics?.avg_gpu_util_30d,
                    power_usage: metrics?.power_usage,
                    water_temperature: metrics?.water_temperature,
                    temperature_history: metrics?.temperature_history,
                    power: metrics?.power,
                    internet: metrics?.internet,
                    machine_id: metrics?.device_id,
                    heatingStatus: metrics?.heating_status,
                    computingStatus: metrics?.computing_status,
                }),
            );
            setCachedMetrics({
                power_sensor_timestamp: metrics?.power_timestamp,
                water_sensor_timestamp: metrics?.sensor_timestamp,
                power_usage: metrics?.power_usage,
                water_temperature: metrics?.water_temperature,
                api_fetch_timestamp: new Date(),
            });
        }
    }, [dispatch, userId, machineId, token]);

    React.useEffect(() => {
        const update = () => {
            setPowerUpdatedAt(getTimeAgo(cachedMetrics?.power_sensor_timestamp));
        };
        update(); // Initial update
        const interval = setInterval(update, 25000); // Update every 1/2 minute
        return () => clearInterval(interval);
    }, [cachedMetrics?.power_sensor_timestamp]);

    React.useEffect(() => {
        const update = () => setWaterUpdatedAt(getTimeAgo(cachedMetrics?.water_sensor_timestamp));
        update(); // Initial update
        const interval = setInterval(update, 25000); // Update every 1/2 minute
        return () => clearInterval(interval);
    }, [cachedMetrics?.water_sensor_timestamp]);

    const fetchDashboardMetricsFromCache = React.useCallback(async () => {
        const metrics = await fetchDashboardMetrics(true);
        if (metrics) {
            const metricsResponse = {
                power_sensor_timestamp: metrics?.power_timestamp,
                water_sensor_timestamp: metrics?.sensor_timestamp,
                water_temperature: metrics?.water_temperature,
                power_usage: metrics?.power_usage,
            };
            if (userId && machineId) {
                dispatch(setCachedMetricsData({ [`${userId}_${machineId}`]: metricsResponse }));
            }
            setCachedMetrics(metricsResponse);
        }
    }, [dispatch, userId, machineId]);
    React.useEffect(() => {
        if (cachedData && Object.keys(cachedData).length !== 0 && cachedData[`${userId}_${machineId}`] !== null) {
            const cachedMetricsData = cachedData[`${userId}_${machineId}`];
            if (cachedMetricsData) {
                setCachedMetrics(cachedMetricsData);
            }
        } else {
            fetchDashboardMetricsFromCache();
        }
    }, [cachedData, fetchDashboardMetricsFromCache, machineId, userId]);

    React.useEffect(() => {
        if (cachedMetrics && cachedMetrics !== null) {
            const dayDiff = getDayDiff(cachedMetrics?.api_fetch_timestamp, new Date()?.toISOString());
            if (dayDiff > 0 || !cachedMetrics?.api_fetch_timestamp) {
                fetchData();
            }
        }
    }, [cachedMetrics, isFocused]);

    const handleDashboardData = React.useCallback(
        (incomingData: Partial<DashboardMetricsData>) => {
            console.warn(incomingData, 'incomingData from socket');
            // Combine all updates into a single state update
            setCachedMetrics((prev: any) => {
                const newState = { ...prev };

                // Base updates
                Object.assign(newState, incomingData);

                // Specific updates based on data type
                if ('internet' in incomingData) {
                    newState.water_sensor_timestamp = incomingData.sensor_timestamp;
                    newState.water_temperature = incomingData.water_temperature;
                    newState.computingStatus = incomingData.computing_status;
                    dispatch(
                        setMetricsData({
                            internet: incomingData?.internet,
                            computingStatus: incomingData?.computing_status,
                        }),
                    );
                }

                if ('power' in incomingData) {
                    newState.power_sensor_timestamp = incomingData.power_timestamp;
                    newState.power_usage = incomingData.power_usage;
                    newState.heating_status = incomingData.heating_status;
                    dispatch(
                        setMetricsData({ power: incomingData?.power, heatingStatus: incomingData?.heating_status }),
                    );
                }
                if (userId && machineId) {
                    // Update the cached metrics in Redux
                    dispatch(setCachedMetricsData({ [`${userId}_${machineId}`]: newState }));
                }

                return newState;
            });
        },
        [userId, machineId, dispatch],
    ); // Add proper dependencies

    useSocket(handleDashboardData);

    React.useEffect(() => {
        if (token && isRefresh) {
            fetchData();
            WSInstance.disconnect();
            WSInstance.connect(token);
            WSInstance?.on(`dashboard/${machineId}/metrics`, data => {
                handleDashboardData(data);
            });
            dispatch(setIsRefresh(false));
        }
    }, [isRefresh, dispatch, fetchData, handleDashboardData, machineId, token]);

    const getMonthlyEarnings = () => {
        const gpuEarnings = earningsData?.gpu_earning_month ?? 0;
        const storageEarnings = earningsData?.storage_earning_month ?? 0;
        const earnings = gpuEarnings + storageEarnings;
        if (earnings === 0) return '$0';
        if (!earningsData) return '--';
        return `$${earnings.toFixed(2)}`;
    };
    const getTotalEarnings = () => {
        const gpuEarnings = earningsData?.gpu_earning_total ?? 0;
        const storageEarnings = earningsData?.storage_earning_total ?? 0;
        const earnings = gpuEarnings + storageEarnings;
        if (earnings === 0) return '$0';
        if (!earningsData) return '--';
        return `$${earnings.toFixed(2)}`;
    };
    const displayWaterTemperature = () => {
        if (cachedMetrics?.water_temperature === null || cachedMetrics?.water_temperature === undefined) {
            return '--';
        }
        const temp = cachedMetrics?.water_temperature;
        return `${temp}${temp > 0 ? '°F' : ''}`;
    };

    const getWaterTempColor = (value: number) => {
        let colorValue = gradientColorArray?.[0];
        const findIndex = rangeArray?.findIndex((item: number) => item == value);
        if (findIndex > -1) {
            colorValue = rangeColorArray?.[findIndex] || colorValue;
        }
        if (value > tempStaticData?.maxValue) {
            colorValue = gradientColorArray?.[gradientColorArray?.length - 1];
        }
        return colorValue;
    };

    return (
        <StyleWrapper className={`cstDashboard ${showHeater && 'indexBodyLoader'}`}>
            <Box textAlign="center" className="indexBody">
                {isMetricsAPICalled && (
                    <Grid2 container height="100%">
                        {!showHeater && (
                            <Grid2 size={{ xs: 12, lg: 5 }} className="listBlock" px={2}>
                                <ListItemWrapper
                                    p={2}
                                    className="wrapperItems"
                                    bgcolor="info.50"
                                    boxShadow={1}
                                    borderRadius={4}
                                    mt={2}
                                >
                                    <Typography variant="subtitle2" align="left" color="info.200">
                                        Machine: {earningsData?.machine_name}
                                    </Typography>
                                    <Stack direction="row" justifyContent="space-between">
                                        <Box textAlign="start">
                                            <Typography variant="h4" fontFamily="good_sansbold" color="primary.main">
                                                {getMonthlyEarnings()}
                                            </Typography>
                                            <Typography variant="subtitle2" pb={1} color="info.200">
                                                {t('this_month')}
                                            </Typography>
                                        </Box>
                                        <Box textAlign="end">
                                            <Typography variant="h4" fontFamily="good_sansbold" color="primary.main">
                                                {getTotalEarnings()}
                                            </Typography>
                                            <Typography variant="subtitle2" pb={1} color="info.200">
                                                {t('life_time')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Box display="flex" pt={1} alignItems="center" justifyContent="space-between">
                                        <Typography
                                            variant="subtitle2"
                                            display="flex"
                                            alignItems="center"
                                            columnGap={1.5}
                                            fontSize="8px"
                                            fontFamily="good_sansbold"
                                            color="primary.main"
                                        >
                                            MY EARNINGS
                                        </Typography>
                                        <Box display="flex" alignItems="center">
                                            <Typography
                                                variant="subtitle2"
                                                display="flex"
                                                alignItems="center"
                                                color="info.200"
                                            >
                                                {t('last_updated')}:
                                            </Typography>
                                            <Typography variant="subtitle2" fontWeight={600} ml={0.5} color="info.200">
                                                {getTimeAgo(earningsData?.gpu_earnings_updated_at)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </ListItemWrapper>
                                <Box pt={4} display="flex" alignItems="center" justifyContent="space-between">
                                    <Typography
                                        variant="subtitle2"
                                        display="flex"
                                        alignItems="center"
                                        columnGap={1.5}
                                        fontFamily="good_sansbold"
                                        color="info.300"
                                    >
                                        {t('watter_readings')}
                                    </Typography>
                                </Box>
                                <ListItemWrapper
                                    className="wrapper"
                                    p={2}
                                    bgcolor="info.50"
                                    boxShadow={1}
                                    borderRadius={4}
                                    mt={2}
                                >
                                    <Stack direction="row" justifyContent="space-between">
                                        <Box textAlign="start">
                                            <Typography variant="h4" fontWeight={700}>
                                                {metricsData?.avg_gpu_util_7d !== undefined &&
                                                metricsData.avg_gpu_util_7d >= 0
                                                    ? `${metricsData.avg_gpu_util_7d}%`
                                                    : '--'}
                                            </Typography>
                                            <Typography variant="subtitle2" pb={1} className="subtext" color="info.400">
                                                {t('gps_utilization_last_seven_days')}
                                            </Typography>
                                        </Box>
                                        <Box textAlign="end" zIndex={1}>
                                            <Typography variant="h4" fontWeight={700}>
                                                {metricsData?.avg_gpu_util_30d !== undefined &&
                                                metricsData.avg_gpu_util_30d >= 0
                                                    ? `${metricsData?.avg_gpu_util_30d}%`
                                                    : '--'}
                                            </Typography>
                                            <Typography variant="subtitle2" pb={1} className="subtext" color="info.400">
                                                {t('gps_utilization_last_thirty_days')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Box pt="3px" display="flex" alignItems="center" justifyContent="space-between">
                                        <Typography
                                            variant="subtitle2"
                                            display="flex"
                                            alignItems="center"
                                            columnGap={1.5}
                                            fontSize="8px"
                                            fontFamily="good_sansbold"
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            {t('gpu_util')}
                                        </Typography>
                                    </Box>
                                    <MemoryIcon />
                                </ListItemWrapper>
                                <Grid2 container spacing={2}>
                                    <Grid2 size={6}>
                                        <ListItemWrapper
                                            className="wrapper"
                                            p={2}
                                            bgcolor="info.50"
                                            boxShadow={1}
                                            borderRadius={4}
                                            mt={2}
                                        >
                                            <Stack direction="row" justifyContent="space-between">
                                                <Box textAlign="start">
                                                    <Typography
                                                        variant="h4"
                                                        fontWeight={700}
                                                        minHeight={28}
                                                        display="flex"
                                                        alignItems="flex-end"
                                                        gap={0.5}
                                                    >
                                                        {cachedMetrics?.power_usage === null ||
                                                        cachedMetrics?.power_usage === undefined
                                                            ? '--'
                                                            : cachedMetrics?.power_usage}
                                                        {cachedMetrics?.power_usage !== null &&
                                                            cachedMetrics?.power_usage !== undefined && (
                                                                <Typography fontWeight={700} fontSize={12}>
                                                                    (W)
                                                                </Typography>
                                                            )}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <Box
                                                pb="15px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Typography
                                                    fontFamily="good_sansbold"
                                                    variant="subtitle2"
                                                    display="flex"
                                                    alignItems="center"
                                                    columnGap={1.5}
                                                    // fontSize={'8px'}
                                                    sx={{ textTransform: 'uppercase' }}
                                                >
                                                    {t('power_usage')}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center">
                                                <Typography variant="subtitle2" display="flex" alignItems="center">
                                                    {t('last_updated_on')}:
                                                </Typography>
                                                <Typography variant="subtitle2" fontWeight={600} ml={0.5}>
                                                    {powerUpdatedAt}
                                                </Typography>
                                            </Box>
                                            <ElectricalServicesOutlinedIcon />
                                        </ListItemWrapper>
                                    </Grid2>
                                    <Grid2 size={6}>
                                        <ListItemWrapper
                                            className="wrapper"
                                            p={2}
                                            bgcolor="info.50"
                                            boxShadow={1}
                                            borderRadius={4}
                                            mt={2}
                                        >
                                            <Stack direction="row" justifyContent="space-between">
                                                <Box textAlign="start">
                                                    <Typography
                                                        variant="h4"
                                                        fontWeight={700}
                                                        sx={{
                                                            color: cachedMetrics?.water_temperature
                                                                ? getWaterTempColor(cachedMetrics?.water_temperature)
                                                                : '',
                                                        }}
                                                    >
                                                        {displayWaterTemperature()}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                            <Box
                                                pb="15px"
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Typography
                                                    fontFamily="good_sansbold"
                                                    variant="subtitle2"
                                                    display="flex"
                                                    alignItems="center"
                                                    columnGap={1.5}
                                                    // fontSize={'8px'}
                                                    sx={{ textTransform: 'uppercase' }}
                                                >
                                                    {t('Water_temperature')}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" alignItems="center">
                                                <Typography variant="subtitle2" display="flex" alignItems="center">
                                                    {t('last_updated_on')}:
                                                </Typography>
                                                <Typography variant="subtitle2" fontWeight={600} ml={0.5}>
                                                    {waterUpdatedAt}
                                                </Typography>
                                            </Box>
                                            <DeviceThermostatOutlinedIcon />
                                        </ListItemWrapper>
                                    </Grid2>
                                </Grid2>
                                {chartData.labels.length > 0 && (
                                    <ListItemWrapper
                                        px={2}
                                        py={3}
                                        className="wrapper"
                                        bgcolor="info.50"
                                        boxShadow={1}
                                        borderRadius={4}
                                        mt={2}
                                    >
                                        <ReactChart chartData={chartData} />
                                    </ListItemWrapper>
                                )}
                                {chartData.labels.length === 0 && (
                                    <ListItemWrapper
                                        px={2}
                                        py={3}
                                        className="wrapper"
                                        bgcolor="info.50"
                                        boxShadow={1}
                                        borderRadius={4}
                                        mt={2}
                                    >
                                        <ReactChart chartData={{ labels: [], values: [] }} />
                                    </ListItemWrapper>
                                )}
                                <ListItemWrapper
                                    px={2}
                                    py={3}
                                    className="wrapper"
                                    bgcolor="info.50"
                                    boxShadow={1}
                                    borderRadius={4}
                                    mt={2}
                                >
                                    <Stack alignItems="flex-start">
                                        <Typography variant="h4" fontFamily="good_sansbold">
                                            {t('hybrid')}
                                        </Typography>
                                        <Typography
                                            pt={0.75}
                                            variant="subtitle2"
                                            pb={1}
                                            sx={{ textTransform: 'uppercase' }}
                                        >
                                            {t('system_mode')}
                                        </Typography>
                                    </Stack>
                                    <BoltOutlinedIcon />
                                </ListItemWrapper>
                            </Grid2>
                        )}
                        <Grid2
                            size={showHeater ? 12 : 7}
                            display={{ xs: 'none', lg: 'block' }}
                            className={`assetBlock ${showHeater ? '' : 'rightImgBlock'}`}
                            pr={2}
                        >
                            {showHeater && (
                                <Typography
                                    variant="h4"
                                    sx={{ marginRight: '30px', fontWeight: '700', fontSize: '24px' }}
                                >
                                    {t('installation_pending')}
                                </Typography>
                            )}
                            <Box />
                            {/* <Box m={'auto'} width={showHeater ? '500px' : '33%'}><img src={assetLoader} width={'100%'} /></Box> */}
                        </Grid2>
                    </Grid2>
                )}
            </Box>
        </StyleWrapper>
    );
};
